import React, { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Button,
  Heading,
  Image,
  Container,
  Wrap,
  Flex,
} from '@chakra-ui/react'

import List from '@/features/otherServices/components/Product/List'

import { ServiceCatalogProps } from './types'
import {
  containerStyle,
  headerContainerStyle,
  itemsWrapperStyle,
  tabButtonStyle,
  readMoreButtonStyle,
  contentContainerStyle,
  textColumnStyle,
  imageColumnStyle,
  imageWrapperStyle,
  priceBoxStyle,
  sectionBoxStyle,
  registerButtonStyle,
  priceTextStyle,
  priceDetailTextStyle,
} from './styles'

const ServiceCatalog = ({ title, items }: ServiceCatalogProps) => {
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    if (items && items.length > 0 && !activeId) {
      setActiveId(items[0].id)
    }
  }, [items, activeId])

  const scrollToSection = (id: string) => {
    setActiveId(id)
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  if (!items || items.length === 0) return null

  return (
    <Box as="section" sx={containerStyle}>
      <Container maxW="container.xl">

        <Box sx={headerContainerStyle}>
          <Heading
            as="h2"
            size="xl"
            color="gray.800"
            textAlign="center"
          >
            {title}
          </Heading>
          <Wrap spacing="24px" justify="center">
            {items.map((item) => (
              <Button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                sx={tabButtonStyle(activeId === item.id)}
                variant="outline"
              >
                {item.topic}
              </Button>
            ))}
          </Wrap>
        </Box>

        <Box sx={itemsWrapperStyle}>
          {items.map((activeContent) => (
            <Box
              key={activeContent.id}
              id={activeContent.id}
              sx={contentContainerStyle}
            >
              <Box sx={textColumnStyle}>
                <Box sx={sectionBoxStyle}>
                  <Heading as="h3" size="lg" color="gray.800" fontWeight="medium">
                    {activeContent.topic}
                  </Heading>
                  <Text fontSize="lg" color="gray.800" fontWeight="normal">
                    {activeContent.description}
                  </Text>
                  {activeContent.buttonLink && activeContent.readMoreLabel && (
                    <Button
                      as="a"
                      href={activeContent.buttonLink}
                      sx={readMoreButtonStyle}
                      variant="unstyled"
                      rightIcon={
                        activeContent.readMoreIcon ? (
                          <Image
                            src={activeContent.readMoreIcon}
                            alt=""
                            boxSize="20px"
                            objectFit="contain"
                          />
                        ) : undefined
                      }
                    >
                      {activeContent.readMoreLabel}
                    </Button>
                  )}
                </Box>

                <Box sx={sectionBoxStyle}>
                  <Heading as="h4" size="md" fontWeight="medium">
                    {activeContent.benefitTitle}
                  </Heading>
                  <List
                    key={activeContent.id}
                    items={activeContent.benefits}
                    bgColor="primary.500"
                  />
                </Box>

                <Box sx={sectionBoxStyle}>
                  <Heading as="h4" size="md" fontWeight="medium">
                    {activeContent.targetTitle}
                  </Heading>
                  <Text color="gray.800" fontSize="lg" fontWeight="normal">
                    {activeContent.targetDescription}
                  </Text>
                </Box>

                <Box sx={priceBoxStyle}>
                  <Flex
                    direction={{ base: 'column', sm: 'row' }}
                    justify="space-between"
                    align={{ base: 'start', sm: 'center' }}
                    gap={4}
                  >
                    <Box>
                      <Text sx={priceTextStyle}>
                        {activeContent.price}
                      </Text>
                      <Text sx={priceDetailTextStyle}>
                        {activeContent.priceDetail}
                      </Text>
                    </Box>
                    <Button
                      as="a"
                      href={activeContent.registerLink}
                      variant="unstyled"
                      sx={registerButtonStyle}
                    >
                      {activeContent.registerButtonLabel}
                    </Button>
                  </Flex>
                </Box>

              </Box>

              <Box sx={imageColumnStyle}>
                <Box sx={imageWrapperStyle}>
                  <Image
                    src={activeContent.imageUrl}
                    alt={activeContent.topic}
                    objectFit="cover"
                    w="full"
                    h="full"
                    transition="transform 0.5s"
                    _hover={{ transform: 'scale(1.05)' }}
                    fallbackSrc="https://via.placeholder.com/600x800?text=Service+Image"
                  />
                </Box>
              </Box>

            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  )
}

export default ServiceCatalog
